import { motion } from "framer-motion";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Phone, ArrowRight, ArrowLeft, CheckCircle } from "lucide-react";
import Layout from "@/components/Layout";
import { useLanguage } from "@/contexts/LanguageContext";
import maintenanceImage from "@/assets/maintenance.jpg";
import accessibilityImage from "@/assets/accessibility.jpg";
import freightImage from "@/assets/freight.jpg";
import modernizationImage from "@/assets/modernization.jpg";

type ServiceContent = {
  title: string;
  subtitle: string;
  description: string[];
  features: string[];
};

type ServiceEntry = {
  image: string;
  el: ServiceContent;
  en: ServiceContent;
};

const servicesData: Record<string, ServiceEntry> = {
  installation: {
    image: modernizationImage,
    el: {
      title: "Μελέτη & Εγκατάσταση Ανελκυστήρων",
      subtitle: "Πλήρης σχεδιασμός και εγκατάσταση",
      description: [
        "Καλύπτουμε όλη την κλίμακα των υδραυλικών ή μηχανοκίνητων ανελκυστήρων, επιβατών ή φορτίων, με μηχανοστάσιο (MR machine room) χωρίς μηχανοστάσιο (MRL machine room less), με ταχύτητες που ξεκινάνε από 0,30 m/s έως 8,50 m/s.",
        "Η εταιρεία μας αναλαμβάνει τη μελέτη, σχεδιασμό και εγκατάσταση ανελκυστήρων υψηλής ποιότητας για κάθε τύπο κτιρίου, από κατοικίες μέχρι εμπορικά κέντρα και ξενοδοχεία.",
      ],
      features: [
        "Υδραυλικοί ανελκυστήρες",
        "Μηχανοκίνητοι ανελκυστήρες",
        "Ανελκυστήρες με μηχανοστάσιο (MR)",
        "Ανελκυστήρες χωρίς μηχανοστάσιο (MRL)",
        "Ταχύτητες από 0,30 m/s έως 8,50 m/s",
        "Πιστοποίηση DIN ISO 9001/1992",
      ],
    },
    en: {
      title: "Elevator Design & Installation",
      subtitle: "Complete design and installation",
      description: [
        "We cover the full range of hydraulic or traction elevators for passengers or freight, with machine room (MR) or without (MRL), with speeds from 0.30 m/s up to 8.50 m/s.",
        "Our company undertakes the study, design and installation of high-quality elevators for every type of building, from residential properties to shopping centers and hotels.",
      ],
      features: [
        "Hydraulic elevators",
        "Traction elevators",
        "Machine room elevators (MR)",
        "Machine-room-less elevators (MRL)",
        "Speeds from 0.30 m/s to 8.50 m/s",
        "DIN ISO 9001/1992 certification",
      ],
    },
  },
  maintenance: {
    image: maintenanceImage,
    el: {
      title: "Συντήρηση & Επισκευή Ανελκυστήρων",
      subtitle: "Τακτική συντήρηση για ασφαλή λειτουργία",
      description: [
        "Ο ανελκυστήρας που παραμένει για μεγάλο χρονικό διάστημα ασυντήρητος δεν είναι ασφαλής!",
        "Η συντήρηση κάθε ανελκυστήρα αποτελεί απαραίτητη προϋπόθεση για την ομαλή και ασφαλή λειτουργία του, αλλά είναι και υποχρεωτική από το νόμο.",
        "Η εταιρεία μας LIFTCO Ε.Ε. διαθέτοντας σύγχρονο εξοπλισμό και έμπειρο εξειδικευμένο ανθρώπινο δυναμικό, αναλαμβάνει με συνέπεια και επαγγελματισμό την συντήρηση και επισκευή του ανελκυστήρα σας σε οικονομικές τιμές.",
      ],
      features: [
        "Τακτικός προληπτικός έλεγχος",
        "Άμεση επισκευή βλαβών",
        "24ωρη εξυπηρέτηση",
        "Αντικατάσταση ανταλλακτικών",
        "Έκδοση πιστοποιητικών",
        "Οικονομικές τιμές",
      ],
    },
    en: {
      title: "Elevator Maintenance & Repair",
      subtitle: "Regular maintenance for safe operation",
      description: [
        "An elevator left unmaintained for a long period is not safe.",
        "Maintenance is essential for smooth and safe operation and is also required by law.",
        "With modern equipment and an experienced technical team, LIFTCO provides maintenance and repairs at competitive prices.",
      ],
      features: [
        "Preventive inspections",
        "Fast fault repair",
        "24/7 service",
        "Spare parts replacement",
        "Certificates issuance",
        "Competitive pricing",
      ],
    },
  },
  modernization: {
    image: modernizationImage,
    el: {
      title: "Εκσυγχρονισμός Ανελκυστήρων",
      subtitle: "Αναβάθμιση με σύγχρονη τεχνολογία",
      description: [
        "Είμαστε πάντα κοντά σας για να σας προτείνουμε τις καλύτερες λύσεις αναβάθμισης του ανελκυστήρα σας είτε αφορά βελτιώσεις μηχανικών μερών ή εξαρτημάτων ασφαλείας είτε αφορά αισθητικές βελτιώσεις σε θάλαμο και κομβιοδόχους.",
        "Ο εκσυγχρονισμός του ανελκυστήρα σας θα αυξήσει τη διάρκεια ζωής του, θα βελτιώσει την ασφάλεια και θα μειώσει την κατανάλωση ενέργειας.",
      ],
      features: [
        "Αναβάθμιση πίνακα χειρισμού",
        "Νέος θάλαμος και πόρτες",
        "Εξαρτήματα ασφαλείας",
        "Ενεργειακή αναβάθμιση",
        "Μοντέρνα αισθητική",
        "Αύξηση διάρκειας ζωής",
      ],
    },
    en: {
      title: "Elevator Modernization",
      subtitle: "Upgrades with modern technology",
      description: [
        "We propose the best upgrade solutions—mechanical improvements, safety components, and aesthetic upgrades for cabin and fixtures.",
        "Modernization increases lifespan, improves safety and can reduce energy consumption.",
      ],
      features: [
        "Controller upgrade",
        "New cabin and doors",
        "Safety components",
        "Energy efficiency improvements",
        "Modern aesthetics",
        "Extended lifespan",
      ],
    },
  },
  accessibility: {
    image: accessibilityImage,
    el: {
      title: "Συστήματα Ανύψωσης ΑΜΕΑ",
      subtitle: "Λύσεις προσβασιμότητας",
      description: [
        "Αναλαμβάνουμε την τοποθέτηση συστημάτων ανύψωσης ΑΜΕΑ σε εσωτερικό ή σε εξωτερικό χώρο, ανάλογα με τις ανάγκες των ατόμων ή την ιδιαιτερότητα του κτιρίου.",
        "Τα συστήματα μας πληρούν όλες τις προδιαγραφές ασφαλείας και προσφέρουν αξιόπιστη λύση για άτομα με κινητικά προβλήματα.",
      ],
      features: [
        "Πλατφόρμες ανύψωσης",
        "Αναβατόρια σκάλας",
        "Εσωτερική εγκατάσταση",
        "Εξωτερική εγκατάσταση",
        "Προσαρμογή σε κάθε κτίριο",
        "Πλήρης υποστήριξη",
      ],
    },
    en: {
      title: "Accessibility Lifting Systems",
      subtitle: "Accessibility solutions",
      description: [
        "We install accessibility lifting systems indoors or outdoors, depending on needs and building constraints.",
        "Our systems meet safety specifications and provide reliable mobility for people with limited movement.",
      ],
      features: [
        "Lifting platforms",
        "Stair lifts",
        "Indoor installation",
        "Outdoor installation",
        "Adaptation to any building",
        "Full support",
      ],
    },
  },
  freight: {
    image: freightImage,
    el: {
      title: "Ανελκυστήρες Φορτίων",
      subtitle: "Βιομηχανικές λύσεις ανύψωσης",
      description: [
        "Πραγματοποιούμε εγκατάσταση ανελκυστήρων φορτίων και ανύψωσης εμπορευμάτων σε αποθήκες, εμπορικά κέντρα, βιομηχανικές μονάδες και λοιπούς επαγγελματικούς χώρους.",
        "Οι ανελκυστήρες φορτίων μας είναι σχεδιασμένοι για μεγάλα φορτία και εντατική χρήση, με υψηλή αξιοπιστία και ανθεκτικότητα.",
      ],
      features: [
        "Μεγάλη χωρητικότητα φορτίου",
        "Ανθεκτική κατασκευή",
        "Βιομηχανικές προδιαγραφές",
        "Εγκατάσταση σε αποθήκες",
        "Εμπορικά κέντρα",
        "Βιομηχανικές μονάδες",
      ],
    },
    en: {
      title: "Freight Elevators",
      subtitle: "Industrial lifting solutions",
      description: [
        "We install freight elevators for warehouses, shopping centers, industrial units and other professional spaces.",
        "Our freight elevators are designed for heavy loads and intensive use, offering high reliability and durability.",
      ],
      features: [
        "High load capacity",
        "Durable construction",
        "Industrial specifications",
        "Warehouse installations",
        "Shopping centers",
        "Industrial units",
      ],
    },
  },
};

const ServiceDetail = () => {
  const { serviceId } = useParams<{ serviceId: string }>();
  const { language, t } = useLanguage();

  const entry = serviceId ? servicesData[serviceId] : null;
  const service = entry ? entry[language] : null;

  if (!entry || !service) {
    return (
      <Layout>
        <div className="container py-20 text-center">
          <h1 className="font-heading text-3xl font-bold mb-4">{t("notfound.title")}</h1>
          <Button asChild>
            <Link to="/services">
              <ArrowLeft className="h-5 w-5" />
              {t("common.allServices")}
            </Link>
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0">
          <img src={entry.image} alt={service.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-hero-gradient" />
        </div>
        <div className="container relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-primary-foreground/80 hover:text-primary-foreground mb-6 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              {t("common.allServices")}
            </Link>
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-primary-foreground mb-4">
              {service.title}
            </h1>
            <p className="text-xl text-primary-foreground/80">{service.subtitle}</p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-20">
        <div className="container">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="prose prose-lg max-w-none"
              >
                {service.description.map((paragraph, index) => (
                  <p key={index} className="text-muted-foreground leading-relaxed mb-6">
                    {paragraph}
                  </p>
                ))}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="mt-12"
              >
                <h2 className="font-heading text-2xl font-bold text-foreground mb-6">
                  {t("serviceDetail.includes")}
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {service.features.map((feature) => (
                    <div key={feature} className="flex items-center gap-3 bg-muted/50 p-4 rounded-lg">
                      <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                      <span className="text-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Sidebar */}
            <div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                className="bg-card rounded-xl p-8 shadow-card sticky top-24"
              >
                <h3 className="font-heading text-xl font-bold text-foreground mb-4">
                  {t("serviceDetail.requestQuote")}
                </h3>
                <p className="text-muted-foreground mb-6">{t("serviceDetail.requestQuoteText")}</p>
                <div className="space-y-4">
                  <Button asChild variant="hero" size="lg" className="w-full">
                    <a href="tel:2310270290">
                      <Phone className="h-5 w-5" />
                      2310 270290
                    </a>
                  </Button>
                  <Button asChild variant="outline" size="lg" className="w-full">
                    <Link to="/contact">
                      {t("common.contact")}
                      <ArrowRight className="h-5 w-5" />
                    </Link>
                  </Button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary">
        <div className="container text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              {t("common.needHelp")}
            </h2>
            <p className="text-primary-foreground/80 max-w-2xl mx-auto mb-8">
              {language === "en"
                ? "Our team is ready to help you. Contact us today!"
                : "Η ομάδα μας είναι έτοιμη να σας βοηθήσει. Επικοινωνήστε μαζί μας σήμερα!"}
            </p>
            <Button asChild variant="hero" size="lg">
              <a href="tel:2310270290">
                <Phone className="h-5 w-5" />
                {t("common.callNow")}
              </a>
            </Button>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default ServiceDetail;
